import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  cancelTransaction,
  createTransaction,
  listTransactions,
  searchSecurities,
  type NewTransactionInput,
} from "./api";

export const useTransactions = (portfolioId: string | undefined) =>
  useQuery({
    queryKey: ["transactions", portfolioId],
    queryFn: () => listTransactions(portfolioId as string),
    enabled: !!portfolioId,
  });

export function useCreateTransaction(portfolioId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewTransactionInput) => createTransaction(portfolioId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["transactions", portfolioId] });
      void qc.invalidateQueries({ queryKey: ["positions", portfolioId] });
      void qc.invalidateQueries({ queryKey: ["portfolios"] });
    },
  });
}

export function useCancelTransaction(portfolioId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelTransaction(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["transactions", portfolioId] });
      void qc.invalidateQueries({ queryKey: ["positions", portfolioId] });
      void qc.invalidateQueries({ queryKey: ["portfolios"] });
    },
  });
}

/** Debounced security search for the transaction form's picker. */
export function useSecuritySearch(q: string, types?: string[]) {
  return useQuery({
    queryKey: ["securities", q, types],
    queryFn: () => searchSecurities(q, types),
    enabled: q.trim().length > 0 && types !== undefined,
  });
}

export function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}
